import { useLazyQuery } from '@apollo/client/react';
import { useEffect, useMemo, useState } from 'react';
import { Checkbox } from '@mui/material';
import { generateColumnsDefaultConfiguration } from '../../../../components/dataGrid/columnHelpers';
import { GET_CONSULTAS } from '../../../../data/graphQL/queries/AdminRoles/getConsultas';
import { GET_CONSULTAS_ROL, adaptConsultasRol } from '../../../../data/graphQL/queries/AdminRoles/getConsultasRol';
import { IconButton, Tooltip } from '@mui/material';
import DeleteIcon from '@mui/icons-material/DeleteOutlineOutlined';

export default function useRoleConsultas({ roleId, onError }) {
    const [catalogRows, setCatalogRows] = useState([]);
    const [selectedRows, setSelectedRows] = useState([]);
    const [draftRows, setDraftRows] = useState([]);
    const [dialogOpen, setDialogOpen] = useState(false);

    const [getConsultas, { data: dataConsultas, error: errorConsultas, loading: loadingConsultas }] = useLazyQuery(GET_CONSULTAS, {
        fetchPolicy: 'cache-and-network'
    });

    const [getConsultasRol, { data: dataConsultasRol, error: errorConsultasRol, loading: loadingConsultasRol }] = useLazyQuery(GET_CONSULTAS_ROL, {
        fetchPolicy: 'cache-and-network'
    });

    useEffect(() => {
        getConsultas();
    }, []);

    useEffect(() => {
        if (!dataConsultas?.allConsultasActivas?.length) return;
        setCatalogRows(dataConsultas.allConsultasActivas || []);
    }, [dataConsultas]);

    useEffect(() => {
        if (errorConsultas) onError?.('ERRORDB');
    }, [errorConsultas, onError]);

    useEffect(() => {
        if (!roleId || roleId <= 0 || catalogRows.length === 0) return;
        getConsultasRol({ variables: { idRol: roleId } });
    }, [roleId, catalogRows, getConsultasRol]);

    useEffect(() => {
        if (!dataConsultasRol?.consultaRolesByRol || dataConsultasRol.consultaRolesByRol.length === 0) return;
        const rows = adaptConsultasRol(dataConsultasRol);
        setSelectedRows(rows);
        setDraftRows(rows);
    }, [dataConsultasRol]);

    useEffect(() => {
        if (errorConsultasRol) onError?.('ERRORDB');
    }, [errorConsultasRol, onError]);

    const selectedIds = useMemo(
        () => new Set(selectedRows.map((row) => row.idConsulta)),
        [selectedRows]
    );

    const availableRows = useMemo(
        () => catalogRows.filter((row) => !selectedIds.has(row.idConsulta)),
        [catalogRows, selectedIds]
    );

    const getDraftById = (id) => draftRows.find((row) => row.idConsulta === id);

    const toggleConsulta = (row) => {
        const item = getDraftById(row.idConsulta);
        if (item) {
            setDraftRows((current) => current.filter((entry) => entry.idConsulta !== row.idConsulta));
            return;
        }

        setDraftRows((current) => [...current, { ...row }]);
    };

    const toggleDataPrivacy = (row) => {
        const item = getDraftById(row.idConsulta);
        if (!item) {
            setDraftRows((current) => [...current, { ...row, dataPrivacy: true }]);
            return;
        }

        setDraftRows((current) => current.map((entry) => (
            entry.idConsulta === row.idConsulta
                ? { ...entry, dataPrivacy: !item.dataPrivacy }
                : entry
        )));
    };

    const removeSelected = (row) => {
        const filtered = selectedRows.filter((item) => item.idConsulta !== row.idConsulta);
        setSelectedRows(filtered);
        setDraftRows(filtered);
    };

    const openDialog = () => {
        setDraftRows(selectedRows);
        setDialogOpen(true);
    };

    const closeDialog = () => {
        setDraftRows(selectedRows);
        setDialogOpen(false);
    };

    const confirmDialog = () => {
        setSelectedRows(draftRows);
        setDialogOpen(false);
    };

    const selectedColumns = useMemo(() => generateColumnsDefaultConfiguration([
        { field: 'idConsulta', headerName: 'ID', flex: 0.1 },
        { field: 'nombre', headerName: 'Titulo', flex: 0.3 },
        { field: 'descripcion', headerName: 'Descripcion', flex: 0.4 },
        { field: 'dataPrivacy', type: 'boolean', headerName: 'DataPrivacy', flex: 0.1 },
        {
            field: 'accionesNotificacion',
            headerName: 'Acciones',
            flex: 0.08,
            sortable: false,
            filterable: false,
            renderCell: (params) => (
                <Tooltip title='Eliminar'>
                    <IconButton onClick={() => removeSelected(params.row)}>
                        <DeleteIcon />
                    </IconButton>
                </Tooltip>
            )
        }
    ]), [catalogRows, selectedRows]);

    const availableColumns = useMemo(() => generateColumnsDefaultConfiguration([
        {
            field: 'seleccionar',
            headerName: 'Seleccionar',
            flex: 0.05,
            sortable: false,
            renderCell: (params) => (
                <Checkbox
                    checked={Boolean(getDraftById(params.row.idConsulta))}
                    onChange={() => toggleConsulta(params.row)}
                />
            )
        },
        { field: 'idConsulta', flex: 0.1, headerName: 'ID' },
        { field: 'nombre', headerName: 'Titulo' },
        { field: 'descripcion', headerName: 'Descripcion' },
        {
            field: 'dataPrivacyToggle',
            headerName: 'DataPrivacy',
            flex: 0.1,
            sortable: false,
            renderCell: (params) => (
                <Checkbox
                    checked={Boolean(getDraftById(params.row.idConsulta)?.dataPrivacy)}
                    onChange={() => toggleDataPrivacy(params.row)}
                />
            )
        }
    ]), [draftRows]);

    return {
        selectedRows,
        availableRows,
        dialogOpen,
        selectedColumns,
        availableColumns,
        openDialog,
        closeDialog,
        confirmDialog,
        removeSelected,
        loading: loadingConsultas || loadingConsultasRol,
        error: errorConsultas || errorConsultasRol
    };
}
